import { deleteRequest, get, patch, upload } from "../../utils/api";


export const customerList = (data) => {
    const statusQuery =
        data?.status && data.status !== "all"
            ? `&status=${encodeURIComponent(data.status)}`
            : "";
    return get(
        `/admin/customer/list?search=${data?.search || ""}&page=${data.page || 1}&limit=${data.limit || 10}${statusQuery}`
    );
};

/** GET /admin/customer/:customerId — full customer profile for admin detail page */
export const customerById = (customerId) => {
    const seg = customerId == null ? "" : String(customerId).trim();
    if (!seg || seg === "undefined" || seg === "null") {
        return Promise.reject({ data: { message: "Customer id is missing" } });
    }
    return get(`/admin/customer/${encodeURIComponent(seg)}`, {});
};

export const customerStatusChange = (data) => {
    const id = data.id;
    delete data.id
    return patch(`/admin/customer/${id}/suspended-user`, data);
};

export const productList = (data) => {
    const statusQuery =
        data?.status && data.status !== "all"
            ? `&status=${encodeURIComponent(data.status)}`
            : "";
    return get(
        `/admin/items/list?search=${data?.search || ""}&page=${data.page || 1}&limit=${data.limit || 10}${statusQuery}`
    );
};

/** Strip fields that must not be sent in PATCH body (Joi rejects `id`, etc.). */
function adminItemPatchBody(data = {}) {
    const { id, onSuccess, items, ...body } = data;
    void id;
    void onSuccess;
    void items;
    return body;
}

/** PATCH /v1/admin/items/:itemId — quantity, weight & price */
export const productIntakeUpdate = (data) => {
    const id = data?.id;
    if (!id) {
        return Promise.reject({ data: { message: "Product id is missing" } });
    }
    const body = adminItemPatchBody(data);
    const payload = {};
    if (body.quantity !== undefined && body.quantity !== "") payload.quantity = body.quantity;
    if (body.weight !== undefined && body.weight !== "") payload.weight = body.weight;
    if (body.approx_rate !== undefined && body.approx_rate !== "") payload.approx_rate = body.approx_rate;
    if (body.payment_status !== undefined) payload.payment_status = body.payment_status;
    if (body.invoice_of_product !== undefined) payload.invoice_of_product = body.invoice_of_product;
    if (Object.keys(payload).length === 0) {
        return Promise.reject({ data: { message: "At least one field is required" } });
    }
    return patch(`/admin/items/${id}`, payload);
};

/** PATCH /v1/admin/items/:itemId/change-status — status workflow (approve, pending, etc.) */
export const productStatusChange = (data) => {
    const id = data?.id;
    if (!id) {
        return Promise.reject({ data: { message: "Product id is missing" } });
    }
    const body = adminItemPatchBody(data);
    const payload = {};
    if (body.status !== undefined && body.status !== "") {
        payload.status = body.status;
    }
    if (Object.keys(payload).length === 0) {
        return Promise.reject({ data: { message: "Status is required" } });
    }
    return patch(`/admin/items/${id}/change-status`, payload);
};

export const productUploadImages = ({ id, formData }) => {
    return upload(`/admin/items/${id}/upload-images`, formData);
};

export const productDeleteImage = ({ id, imageUrl }) => {
    return deleteRequest(`/admin/items/${id}/images`, { imageUrl });
};

export const addNoteToProduct = (id, data) => {
    return patch(`/admin/items/${id}/add-note`, data);
};

export const getProductNotes = (id) => {
    return get(`/admin/items/${id}/notes`);
};

export const lockerList = (data) => {
    return get(`/admin/virtual-locker/list?search=${data?.search||""}&page=${data.page || 1}&limit=${data.limit || 10}`);
};

export const lockerStatusChange = (data) => {
    const id = data?.id;
    if (!id) {
        return Promise.reject({ data: { message: "Locker id is missing" } });
    }
    const { status } = data;
    if (!status) {
        return Promise.reject({ data: { message: "Status is required" } });
    }
    return patch(`/admin/virtual-locker/${encodeURIComponent(id)}/change-status`, { status });
};

export const orderList = (data) => {
    const statusQuery =
        data?.status && data.status !== "all"
            ? `&status=${encodeURIComponent(data.status)}`
            : "";
    return get(
        `/admin/order/list?search=${data?.search || ""}&page=${data.page || 1}&limit=${data.limit || 10}${statusQuery}`
    );
};

function adminOrderPathSegment(orderId) {
    const id = orderId == null ? "" : String(orderId).trim();
    if (!id || id === "undefined" || id === "null") {
        return null;
    }
    return encodeURIComponent(id);
}

export const adminOrderById = (id) => {
    const seg = adminOrderPathSegment(id);
    if (!seg) {
        return Promise.reject({ data: { message: "Order id is missing" } });
    }
    return get(`/admin/order/${seg}`, {});
};

export const uploadFile = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return upload('/upload/file', formData);
};

export const orderStatusChange = (data) => {
    const { id, onSuccess, items, ...rest } = data;
    return patch(`/admin/order/${id}/change-status`, rest);
};

/** Strip fields that must not be sent on PATCH order charges. */
function adminOrderChargesBody(data = {}) {
    const { id, orderId, onSuccess, items, ...body } = data ?? {};
    return body;
}

/** Admin: PATCH /admin/order/:orderId/charges */
export const adminOrderChargesUpdate = (orderId, data) => {
    const seg = adminOrderPathSegment(orderId);
    if (!seg) {
        return Promise.reject({ data: { message: "Order id is missing" } });
    }
    return patch(`/admin/order/${seg}/charges`, adminOrderChargesBody(data));
};

/** Admin: PATCH /admin/order/:orderId/tracking */
export const adminOrderTrackingUpdate = (orderId, data) => {
    const seg = adminOrderPathSegment(orderId);
    if (!seg) {
        return Promise.reject({ data: { message: "Order id is missing" } });
    }
    const payload = {};
    if (data?.tracking_number !== undefined) payload.tracking_number = data.tracking_number;
    if (data?.carrier !== undefined) payload.carrier = data.carrier;
    if (data?.tracking_url !== undefined) payload.tracking_url = data.tracking_url;
    if (Object.keys(payload).length === 0) {
        return Promise.reject({ data: { message: "At least one tracking field is required" } });
    }
    return patch(`/admin/order/${seg}/tracking`, payload);
};

/** Admin: send final order update to customer (notify + flag on order). */
export const adminOrderFinalUpdate = (orderId, data) => {
    const seg = adminOrderPathSegment(orderId);
    if (!seg) {
        return Promise.reject({ data: { message: "Order id is missing" } });
    }
    return patch(`/admin/order/${seg}/final-update`, data ?? {});
};

/** Admin: PATCH /admin/order/:orderId/package-notes */
export const adminOrderPackageNotesUpdate = (orderId, data) => {
    const seg = adminOrderPathSegment(orderId);
    if (!seg) {
        return Promise.reject({ data: { message: "Order id is missing" } });
    }
    return patch(`/admin/order/${seg}/package-notes`, {
        package_notes: data?.package_notes ?? "",
    });
};

export const groupCartMonitoringList = (data) => {
    return get(`/admin/group-cart-monitoring/list?search=${data?.search || ""}&page=${data.page || 1}&limit=${data.limit || 10}`);
};

export const customerApi = {
    customerList,
    customerById,
    customerStatusChange,
    productList,
    productStatusChange,
    productIntakeUpdate,
    productUploadImages,
    productDeleteImage,
    addNoteToProduct,
    getProductNotes,
    lockerList,
    lockerStatusChange,
    orderList,
    adminOrderById,
    orderStatusChange,
    uploadFile,
    adminOrderChargesUpdate,
    adminOrderTrackingUpdate,
    adminOrderFinalUpdate,
    adminOrderPackageNotesUpdate,
    groupCartMonitoringList,
};
